import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  setDoc,
  addDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";

const TARGET_SITE_UID = "bhzfBvdgxZYrvKvDaXz5iaQzIjn1";

export const logHistory = async ({
  branchName,
  path,
  docId,
  user,
  action,
  previousData,
  newData,
  changedKeys,
}) => {
  try {
    const historyCol = collection(db, "site_history");
    const currentUserEmail = user || auth.currentUser?.email || "admin";
    await addDoc(historyCol, {
      timestamp: Date.now(),
      dateFormatted: new Date().toLocaleString(),
      user: currentUserEmail,
      branchName: branchName || docId,
      path: path || "",
      docId: docId || "",
      action: action || "Updated fields",
      changedKeys: changedKeys || Object.keys(newData || {}),
      previousData: previousData || {},
      newData: newData || {},
    });
    console.log("Logged history checkpoint for:", branchName || docId, action);
  } catch (err) {
    console.error("Error logging history:", err);
  }
};

function HistoryDrawer({ open, onClose, targetBranch, onRollbackComplete }) {
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(targetBranch || "all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (targetBranch) {
      setSelectedBranch(targetBranch);
    }
  }, [targetBranch]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);

    const historyCol = collection(db, "site_history");
    const q = query(historyCol, orderBy("timestamp", "desc"), limit(80));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const records = [];
        snapshot.forEach((docSnap) => {
          records.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
        setHistoryList(records);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading history:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [open]);

  const handleRollback = async (record) => {
    if (
      !window.confirm(
        `Rollback "${record.branchName}" to the state saved on ${record.dateFormatted}?`
      )
    ) {
      return;
    }

    setRollingBack(record.id);
    try {
      if (record.path && record.docId && record.previousData) {
        // 1. Restore individual document
        const targetDocRef = doc(db, record.path, record.docId);
        await setDoc(targetDocRef, record.previousData);

        // 2. Restore summary document entry
        const segments = record.path.split("/").filter(Boolean);
        const rootIndex = segments.indexOf("root");
        let summaryKey = `/${record.docId}`;
        if (rootIndex !== -1) {
          const afterRoot = segments.slice(rootIndex);
          const unique = [];
          for (let i = 0; i < afterRoot.length; i++) {
            if (unique.length === 0 || unique[unique.length - 1] !== afterRoot[i]) {
              unique.push(afterRoot[i]);
            }
          }
          if (!unique.includes(record.docId)) {
            unique.push(record.docId);
          }
          summaryKey = "/" + unique.join("/");
        }

        const summaryRef = doc(db, "trees", "users", TARGET_SITE_UID, "summary");
        await setDoc(summaryRef, { [summaryKey]: record.previousData }, { merge: true });

        // 3. Broadcast live update to iframe preview
        const msg = {
          type: "LIVE_UPDATE",
          summaryKey,
          docData: record.previousData,
        };
        try {
          const bc = new BroadcastChannel("lwong_live_preview");
          bc.postMessage(msg);
          bc.close();
        } catch (e) {}
        window.postMessage(msg, "*");
        document.querySelectorAll("iframe").forEach((f) => {
          try {
            f.contentWindow.postMessage(msg, "*");
          } catch (e) {}
        });

        // 4. Log the rollback event itself
        await logHistory({
          branchName: record.branchName,
          path: record.path,
          docId: record.docId,
          user: auth.currentUser?.email || "admin",
          action: `Rolled back to ${record.dateFormatted}`,
          previousData: record.newData,
          newData: record.previousData,
          changedKeys: Object.keys(record.previousData),
        });

        alert(`Successfully rolled back "${record.branchName}"!`);
        if (onRollbackComplete) onRollbackComplete(record);
      }
    } catch (err) {
      console.error("Rollback error:", err);
      alert("Rollback failed: " + err.message);
    } finally {
      setRollingBack(null);
    }
  };

  if (!open) return null;

  const uniqueBranches = Array.from(
    new Set(historyList.map((r) => r.branchName || r.docId).filter(Boolean))
  ).sort();

  const filteredList = historyList.filter((record) => {
    if (selectedBranch !== "all") {
      const b = (record.branchName || record.docId || "").toLowerCase();
      if (b !== selectedBranch.toLowerCase()) return false;
    }
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      const matchBranch = (record.branchName || record.docId || "").toLowerCase().includes(term);
      const matchAction = (record.action || "").toLowerCase().includes(term);
      const matchUser = (record.user || "").toLowerCase().includes(term);
      const matchFields = (record.changedKeys || []).some((k) =>
        k.toLowerCase().includes(term)
      );
      if (!matchBranch && !matchAction && !matchUser && !matchFields) return false;
    }
    return true;
  });

  return (
    <Overlay onClick={onClose}>
      <DrawerContent onClick={(e) => e.stopPropagation()}>
        <DrawerHeader>
          <HeaderInfo>
            <HeaderTitleRow>
              <DrawerTitle>Global Site History</DrawerTitle>
              <CountBadge>{filteredList.length} records</CountBadge>
            </HeaderTitleRow>
            <DrawerSubtitle>
              Chronological log of edits, uploads, and sub-branches across the entire site
            </DrawerSubtitle>
          </HeaderInfo>
          <CloseButton onClick={onClose} title="Close History">
            ✕
          </CloseButton>
        </DrawerHeader>

        <FilterBar>
          <SearchInput
            type="text"
            placeholder="Search action, user, or field..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <BranchSelect
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
          >
            <option value="all">All Branches ({historyList.length})</option>
            {uniqueBranches.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </BranchSelect>
        </FilterBar>

        <HistoryBody>
          {loading && <LoadingText>Loading revision history...</LoadingText>}

          {!loading && filteredList.length === 0 && (
            <EmptyHistory>
              <EmptyIcon>🕒</EmptyIcon>
              <div>No changes matching filter.</div>
              <EmptyHint>
                Edits, image changes, and newly created branches will appear here in real time.
              </EmptyHint>
            </EmptyHistory>
          )}

          {!loading &&
            filteredList.map((record) => (
              <HistoryCard key={record.id}>
                <CardTop>
                  <TimeUserRow>
                    <Timestamp>{record.dateFormatted}</Timestamp>
                    <UserBadge title="Editor">{record.user || "admin"}</UserBadge>
                  </TimeUserRow>
                  <BranchPill
                    onClick={() => setSelectedBranch(record.branchName || record.docId)}
                    title="Click to filter by this branch"
                  >
                    {record.branchName || record.docId}
                  </BranchPill>
                </CardTop>

                <ActionDescription>{record.action}</ActionDescription>

                {record.changedKeys && record.changedKeys.length > 0 && (
                  <ChangedFieldsList>
                    <span style={{ color: "#aaa" }}>Modified:</span>{" "}
                    {record.changedKeys.map((k) => (
                      <FieldTag key={k}>{k}</FieldTag>
                    ))}
                  </ChangedFieldsList>
                )}

                <RollbackActionRow>
                  <RollbackButton
                    type="button"
                    disabled={rollingBack === record.id}
                    onClick={() => handleRollback(record)}
                  >
                    {rollingBack === record.id
                      ? "Restoring..."
                      : "↺ Rollback to this state"}
                  </RollbackButton>
                </RollbackActionRow>
              </HistoryCard>
            ))}
        </HistoryBody>
      </DrawerContent>
    </Overlay>
  );
}

export default HistoryDrawer;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
`;

const DrawerContent = styled.div`
  width: 480px;
  max-width: 92vw;
  height: 100vh;
  background-color: #1a1a1c;
  border-left: 1px solid #2c2c2e;
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.6);
  animation: slideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 18px 20px 14px 20px;
  background-color: #141416;
  border-bottom: 1px solid #28282b;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DrawerTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #f5f5f7;
  margin: 0;
  letter-spacing: 0.2px;
`;

const CountBadge = styled.span`
  background-color: rgba(255, 255, 255, 0.1);
  color: #aaa;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
`;

const DrawerSubtitle = styled.span`
  font-size: 12px;
  color: #8e8e93;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #8e8e93;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: 0.15s;

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 20px;
  background-color: #161618;
  border-bottom: 1px solid #252528;
`;

const SearchInput = styled.input`
  flex: 1;
  background-color: #222226;
  border: 1px solid #333338;
  border-radius: 6px;
  padding: 7px 10px;
  color: #fff;
  font-size: 12px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder {
    color: #666;
  }

  &:focus {
    border-color: #2a9d8e;
  }
`;

const BranchSelect = styled.select`
  background-color: #222226;
  border: 1px solid #333338;
  border-radius: 6px;
  padding: 7px 10px;
  color: #fff;
  font-size: 12px;
  outline: none;
  cursor: pointer;
  max-width: 160px;
  text-overflow: ellipsis;

  &:focus {
    border-color: #2a9d8e;
  }

  option {
    background-color: #222226;
    color: #fff;
  }
`;

const HistoryBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingText = styled.div`
  color: #8e8e93;
  text-align: center;
  margin-top: 40px;
  font-size: 14px;
`;

const EmptyHistory = styled.div`
  color: #8e8e93;
  text-align: center;
  margin-top: 60px;
  font-size: 13px;
  line-height: 1.5;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  opacity: 0.6;
`;

const EmptyHint = styled.div`
  font-size: 12px;
  color: #666;
  max-width: 280px;
`;

const HistoryCard = styled.div`
  background-color: #222225;
  border: 1px solid #2e2e32;
  border-radius: 8px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3e3e44;
    background-color: #26262a;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const TimeUserRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Timestamp = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #e5e5ea;
`;

const UserBadge = styled.span`
  font-size: 11px;
  color: #8e8e93;
`;

const BranchPill = styled.button`
  background-color: rgba(42, 157, 142, 0.15);
  color: #2a9d8e;
  border: 1px solid rgba(42, 157, 142, 0.35);
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background-color: rgba(42, 157, 142, 0.25);
    border-color: #2a9d8e;
  }
`;

const ActionDescription = styled.div`
  font-size: 13px;
  color: #d1d1d6;
  font-weight: 500;
`;

const ChangedFieldsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  margin-top: 2px;
`;

const FieldTag = styled.span`
  background-color: rgba(255, 255, 255, 0.08);
  color: #bbb;
  font-family: monospace;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 4px;
`;

const RollbackActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

const RollbackButton = styled.button`
  background-color: rgba(255, 255, 255, 0.08);
  color: #e5e5ea;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    background-color: #e63946;
    border-color: #e63946;
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
